import { createContext, useContext } from "react";

// Uniform auth surface for the doctor app (Cognito in prod, dev-session cookie locally). MFA is
// enforced server-side on every /v1/doctor route; step-up freshness gates the Healthie hand-off.
export interface AppAuth {
  isLoaded: boolean;
  isSignedIn: boolean;
  /** Server-side authorization for THIS portal: "checking" while the /v1/doctor/me probe
   *  is in flight, "authorized" on a 200, "denied" on 401/403/network. The shell renders only on
   *  "authorized"; "denied" shows the rejection wall. Meaningless until isSignedIn is true. */
  authz: "checking" | "authorized" | "denied";
  /** Server-confirmed principal role (drives the real role label). Null until the probe resolves. */
  role: string | null;
  displayName: string | null;
  /** Bearer token for the API (cc-doctor audience), or null when a session cookie is used. */
  getToken: () => Promise<string | null>;
  signOut: () => Promise<void> | void;
  /** Dev-only convenience sign-in (cookie). Undefined when Cognito is enabled. */
  devSignIn?: () => Promise<void>;
}

export const AuthContext = createContext<AppAuth | null>(null);

export function useAppAuth(): AppAuth {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAppAuth must be used within an AuthProvider");
  return ctx;
}
